<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <v-row align="center" class="mb-4">
      <v-col cols="12" lg="auto" class="d-flex align-center">
        <report-food-select-food-companies></report-food-select-food-companies>
      </v-col>
      <v-col cols="4" lg="auto" class="d-flex align-center">
        <shift-picker v-model="selectedShifts" multiple />
      </v-col>
      <v-col cols="8" lg="auto" class="d-flex align-center">
        <autocomplete
          :label="$t('meal_categories')"
          v-model="selectedCategories"
          :items="categoryItems"
          item-text="text"
          item-value="value"
          multiple
          hide-details
        ></autocomplete>
      </v-col>
      <v-col cols="auto" class="d-flex align-center">
        <v-checkbox
          hide-details
          v-model="$store.state.report.filters.showCompanyNames"
          :label="$t('show_company_names_print')"
        ></v-checkbox>
      </v-col>
    </v-row>

    <grouped-shift-meals-table
      ref="mealsTable"
      :loading="loading"
      :daily-data="processedDailyData"
      :meal-categories="mealCategories"
      :selected-shift="selectedShifts"
      :selected-categories="selectedCategories"
      :date-array="dateArray"
    />
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasFoodOrders from "@/mixins/report/hasFoodOrders";
import hasPrinting from "@/mixins/hasPrinting";
import ReportFoodSelectFoodCompanies from "@/components/report/fields/ReportFieldSelectFoodCompanies.vue";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasReloadWatch from "@/mixins/report/hasReloadWatch";
import GroupedShiftMealsTable from "@/components/report/shared/GroupedShiftMealsTable.vue";

export default {
  name: "ReportTabGroupMealsTabGroupedShiftMeals",
  mixins: [hasFoodOrders, hasPrinting, hasReportPrinting, hasExcel, hasReloadWatch],
  components: {
    ReportFoodSelectFoodCompanies,
    GroupedShiftMealsTable,
  },
  data() {
    return {
      orders: [],
      selectedShifts: [1, 2, 3, 4], // Po default sve četiri smene
      selectedCategories: [], // Filter za kategorije jela
      loading: false, // Loader za učitavanje podataka
    };
  },
  methods: {
    findUsers() {
      if (!this.filters.dates.from || !this.filters.dates.to) {
        this.orders = [];
        return;
      }

      this.loading = true; // Počni loading

      const params = {
        date: this.filters.dates.from, // Prvi datum
        date2: this.filters.dates.to, // Drugi datum
      };
      reportService
        .getFoodOrdersOrdered(this.$store.state.restaurant.id, params)
        .then((response) => {
          this.setMealCategories(response.data);
          this.orders = response.data;
        })
        .finally(() => {
          this.loading = false; // Završi loading
        });
    },

    generateDateArray(startDate, endDate) {
      const dates = [];
      const start = new Date(startDate);
      const end = new Date(endDate);

      while (start <= end) {
        dates.push(start.toISOString().substr(0, 10));
        start.setDate(start.getDate() + 1);
      }

      return dates;
    },
  },
  mounted() {
    // Učitaj podatke pri pokretanju komponente
    this.findUsers();
  },
  computed: {
    selectedFoodCompanies() {
      return this.$store.state.report.selectedFoodCompanies;
    },
    selectedCompaniesNames() {
      return (this.selectedFoodCompanies || []).map((company) => company.name).join(", ");
    },
    processedDailyData() {
      if (!this.orders || this.orders.length === 0) return {};

      const dailyData = {};

      // Generiši datume iz date range-a
      const dateArray = this.dateArray;

      // Definiši smene na osnovu filtera - sada je niz
      const shiftsToProcess =
        this.selectedShifts && this.selectedShifts.length > 0
          ? this.selectedShifts
          : [1, 2, 3, 4];

      const categoryShiftCombinations = [];
      this.mealCategories.forEach((category) => {
        shiftsToProcess.forEach((shift) => {
          categoryShiftCombinations.push(`${category}_${shift}`);
        });
      });

      // Grupiši podatke po datumima
      dateArray.forEach((date) => {
        dailyData[date] = {};
        categoryShiftCombinations.forEach((combination) => {
          dailyData[date][combination] = 0;
        });
      });

      // Kreraj listu ID-jeva selektovanih kompanija za filtriranje
      const selectedCompanyIds = (this.selectedFoodCompanies || []).map(
        (company) => company.id
      );

      // Saberi količine po datumima, kategorijama i smenama
      dateArray.forEach((date) => {
        this.mealCategories.forEach((category) => {
          shiftsToProcess.forEach((shift) => {
            const combinationKey = `${category}_${shift}`;

            // Filtriraj narudžbe za specifičan datum, kategoriju, smenu I selektovane kompanije
            const filteredOrders = this.orders.filter(
              (order) =>
                order.date === date &&
                order.category_name === category &&
                order.shift === shift &&
                selectedCompanyIds.includes(order.company_id) // Dodaj filter za kompanije
            );

            // Saberi količine za taj datum, kategoriju i smenu
            const totalQuantity = filteredOrders.reduce(
              (sum, order) => sum + (order.quantity || 0),
              0
            );
            dailyData[date][combinationKey] = totalQuantity;
          });
        });
      });

      return dailyData;
    },
    categoryItems() {
      return (this.mealCategories || []).map((category) => ({
        text: this.toLatin(category),
        value: category,
      }));
    },
    dateArray() {
      if (!this.filters.dates.from || !this.filters.dates.to) return [];
      return this.generateDateArray(this.filters.dates.from, this.filters.dates.to);
    },
    filters() {
      return this.$store.state.report.filters;
    },
    selectedCompanies() {
      return this.$store.state.report.selectedFoodCompanies;
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        if (
          new Date(this.filters.dates.to).getTime() <
            new Date(this.filters.dates.from).getTime() ||
          !this.filters.dates.to
        ) {
          this.filters.dates.to = this.filters.dates.from;
        } else {
          this.findUsers();
        }
      },
      immediate: true,
      deep: true,
    },
    mealCategories: {
      handler(newCategories) {
        // Ako su kategorije učitane i selectedCategories je prazan, postavi sve kategorije kao default
        if (
          newCategories &&
          newCategories.length > 0 &&
          (!this.selectedCategories || this.selectedCategories.length === 0)
        ) {
          this.selectedCategories = [...newCategories];
        }
      },
      immediate: true,
    },
    isPrinting() {
      if (this.isPrinting) {
        const totalCompanies = this.$store.state.report.foodCompanies.length;
        const selectedCount = this.selectedCompanies.length;
        const allSelected = totalCompanies === selectedCount;

        // Subtitle with company count
        const companySummary = allSelected
          ? this.$t("for_all_companies")
          : `${this.$t("selected_companies")} (${selectedCount} ${this.$t(
              "of"
            )} ${totalCompanies})`;

        // Footer with company names
        let companiesFooter = "";
        if (this.filters.showCompanyNames) {
          const companyNames = this.selectedCompanies
            .map((company) => this.toLatin(company.name))
            .join(", ");
          companiesFooter = `<div style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd;">
            <strong>${this.$t("companies")}:</strong> ${companyNames}
          </div>`;
        }

        this.printDocument({
          tables: [
            {
              headers: this.$refs.mealsTable.printHeaders,
              items: this.$refs.mealsTable.printItems,
              showCaption: true,
            },
          ],
          title: [this.$t("report"), "Grupa jela"].join(": "),
          subtitle: `${this.subtitle}<h4 style="text-align: center">${companySummary}</h4>`,
          afterTableHtml: companiesFooter,
          hideDatetime: true,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName: [this.$t("report"), "Grupa jela"].join(" ") + ".xlsx",
          sheets: [
            {
              name: "Grupa jela",
              headers: this.$refs.mealsTable.headers,
              items: this.$refs.mealsTable.printItems,
              title: [this.$t("report"), "Grupa jela"].join(" "),
              subtitle: this.subtitle,
              beforeTableHtml: `<h4 style="text-align: center">${this.selectedCompaniesNames}</h4>`,
            },
          ],
        });
      }
    },
  },
};
</script>
